// YERİ: src/app/domains/website/sections/mkt-media.ts
// Vitrinin TEK medya yuvası. Ürün demoları (kısa GIF/video) buradan geçer.
//
// Tüm pazarlama bölümleri aynı config anahtarlarını kullanır — böylece bir
// bölümdeki demo diğerine taşınırken alan adı değişmez:
//
//   mediaType    none | image | gif | video | youtube
//   mediaUrl     görsel/video URL'i (CMS > Medya kütüphanesinden seçilir)
//   mediaPoster  video için kapak görseli (ilk kare yerine)
//   mediaAlt     erişilebilirlik metni
//   mediaCaption medyanın altındaki açıklama
//
// 'none' YER TUTUCU gösterir: bölüm demosuz da düzgün görünür, GIF'ler sonradan
// doldurulur. Vitrini içerik hazır olmadan kurabilmenin yolu bu.

import { isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, input, PLATFORM_ID, signal } from '@angular/core';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { AppMockVariant, MktAppMock } from './mkt-app-mock';
import { MktChromeText } from '../core/mkt-chrome-text';

export type MktMediaType = 'none' | 'image' | 'gif' | 'video' | 'youtube';

@Component({
  selector: 'mkt-media',
  standalone: true,
  imports: [MktAppMock],
  template: `
    <figure class="w-full">
      @switch (type()) {
        @case ('video') {
          <!-- Sessiz, döngülü, satır-içi: bir GIF gibi davranır ama onda biri ağırlığında.
               Hareket-azaltma modunda sadece poster kalır (aşağıdaki playable() false olur). -->
          @if (playable()) {
            <video
              class="w-full rounded-xl border border-neutral-200 shadow-2xl shadow-neutral-900/10 dark:border-neutral-800"
              [src]="url()"
              [poster]="poster() || null"
              [attr.aria-label]="alt() || null"
              autoplay muted loop playsinline preload="metadata"></video>
          } @else if (poster()) {
            <img
              class="w-full rounded-xl border border-neutral-200 shadow-2xl shadow-neutral-900/10 dark:border-neutral-800"
              [src]="poster()" [alt]="alt()" loading="lazy" decoding="async" />
          }
        }

        @case ('youtube') {
          <!-- Facade: iframe'i ancak tıklanınca yükleriz. YouTube'un gömme scripti
               ~1 MB ve üçüncü-taraf çerez basar; ana sayfanın Lighthouse skorunu
               tek başına düşürür. Tıklamayan ziyaretçi hiç ödemez. -->
          @if (ytPlaying()) {
            <iframe
              class="aspect-video w-full rounded-xl border border-neutral-200 shadow-2xl dark:border-neutral-800"
              [src]="ytEmbedUrl()"
              [title]="alt() || text.t('video')"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen></iframe>
          } @else {
            <button type="button" (click)="playYoutube()"
              class="group relative block aspect-video w-full overflow-hidden rounded-xl border border-neutral-200 shadow-2xl dark:border-neutral-800">
              @if (poster() || ytThumb()) {
                <img class="size-full object-cover" [src]="poster() || ytThumb()" [alt]="alt()"
                  loading="lazy" decoding="async" />
              }
              <span class="absolute inset-0 grid place-items-center bg-neutral-900/30 transition group-hover:bg-neutral-900/45">
                <span class="grid size-16 place-items-center rounded-full bg-white/95 shadow-lg transition group-hover:scale-110">
                  <!-- 'ml-1' BİLEREK fiziksel: üçgen ikonun optik ortalaması.
                       Oynat simgesi RTL dillerde de sağa bakar (medya oynatıcı
                       geleneği), dolayısıyla bu kaydırma yön değiştirmemeli. -->
                  <svg class="ml-1 size-6 fill-neutral-900" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                </span>
              </span>
              <span class="sr-only">{{ text.t('playVideo') }}</span>
            </button>
          }
        }

        @case ('none') {
          <!-- Medya YÜKLENMEMİŞ. Boş kesikli kutu göstermek sayfayı wireframe'e
               çevirir — bunun yerine ürünün KENDİSİNİ kodla çiziyoruz. Gerçek
               demo GIF'i geldiğinde mediaType değişir ve bu dal devre dışı kalır. -->
          @if (mock(); as variant) {
            <mkt-app-mock [variant]="variant" />
          } @else if (showPlaceholder()) {
            <div class="grid aspect-video w-full place-items-center rounded-xl border-2 border-dashed border-[color:var(--mkt-border)]">
              <p class="text-sm text-[color:var(--mkt-text-faint)]">{{ text.t('mediaSlot') }}</p>
            </div>
          }
        }

        @default {
          <!-- image | gif — GIF de bir <img>; ayrı bir dala gerek yok. -->
          @if (url()) {
            <img
              class="w-full rounded-xl border border-neutral-200 shadow-2xl shadow-neutral-900/10 dark:border-neutral-800"
              [src]="url()" [alt]="alt()"
              [attr.loading]="eager() ? null : 'lazy'"
              [attr.fetchpriority]="eager() ? 'high' : null"
              decoding="async" />
          }
        }
      }

      @if (caption()) {
        <figcaption class="mt-3 text-center text-sm text-neutral-500">{{ caption() }}</figcaption>
      }
    </figure>
  `,
})
export class MktMedia {
  /** Vitrin kabuk metinleri — dil `/:lang` rotasindan. */
  protected text = inject(MktChromeText);
  readonly config = input.required<SiteConfig>();
  /**
   * Hero gibi ekranın üstündeki medya için true — lazy YÜKLENMEZ, öncelikli
   * çekilir. LCP (Largest Contentful Paint) çoğu zaman tam da bu görseldir;
   * lazy bırakmak Lighthouse'ta doğrudan puan kaybettirir.
   */
  readonly eager = input<boolean>(false);
  /** Editör önizlemesinde boş yuvayı çerçeveyle göster. */
  readonly showPlaceholder = input<boolean>(false);

  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  protected ytPlaying = signal(false);

  protected type = computed<MktMediaType>(() => {
    const t = cfg<string>(this.config(), 'mediaType', 'none');
    const url = cfg<string>(this.config(), 'mediaUrl', '');

    // Tip verilmemiş ama URL varsa uzantıdan çıkar — editörün tipi seçmeyi
    // unutması yüzünden demo kaybolmasın.
    if ((t === 'none' || !t) && url) {
      if (/\.(mp4|webm)(\?|$)/i.test(url)) return 'video';
      if (/youtu\.?be/i.test(url)) return 'youtube';
      return 'image';
    }
    return (t as MktMediaType) || 'none';
  });

  protected url     = computed(() => cfg<string>(this.config(), 'mediaUrl', ''));
  protected poster  = computed(() => cfg<string>(this.config(), 'mediaPoster', ''));
  protected alt     = computed(() => cfg<string>(this.config(), 'mediaAlt', ''));
  protected caption = computed(() => cfg<string>(this.config(), 'mediaCaption', ''));

  /**
   * Medya yokken çizilecek ürün mockup'ı. Boşsa hiçbir şey çizilmez —
   * "yok" demek bilinçli bir seçim olabilir (yalnız-metin bölüm).
   */
  private static readonly MOCKS: AppMockVariant[] =
    ['dashboard', 'calendar', 'crm', 'invoice', 'website'];

  protected mock = computed<AppMockVariant | null>(() => {
    const value = cfg<string>(this.config(), 'mockVariant', '');
    return MktMedia.MOCKS.includes(value as AppMockVariant)
      ? (value as AppMockVariant)
      : null;
  });

  /**
   * Otomatik oynatma yalnız tarayıcıda ve kullanıcı hareketi azaltmayı
   * seçmediyse. Sunucuda false — <video autoplay> SSR HTML'ine girmez.
   */
  protected playable = computed(() => {
    if (!this.isBrowser) return false;
    if (typeof matchMedia === 'undefined') return true;
    return !matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  protected ytId = computed(() => {
    const url = this.url();
    const m = /(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/.exec(url);
    return m ? m[1] : '';
  });

  protected ytThumb    = computed(() => this.ytId() ? `https://i.ytimg.com/vi/${this.ytId()}/hqdefault.jpg` : '');
  protected ytEmbedUrl = computed(() => this.ytId() ? `https://www.youtube-nocookie.com/embed/${this.ytId()}?autoplay=1` : '');

  protected playYoutube(): void {
    this.ytPlaying.set(true);
  }
}

/**
 * Bölüm şemalarına eklenecek ortak medya alanları. Her pazarlama bölümü bunu
 * yayarak aynı yuvayı kazanır — alan adları tek yerde tanımlı kalır.
 */
export const MKT_MEDIA_FIELDS = [
  {
    key: 'mediaType', label: 'Medya Türü', type: 'select' as const,
    options: [
      { value: 'none',    label: 'Yok — ürün mockup\'ı çiz' },
      { value: 'image',   label: 'Görsel' },
      { value: 'gif',     label: 'GIF' },
      { value: 'video',   label: 'Video (mp4/webm — sessiz döngü)' },
      { value: 'youtube', label: 'YouTube' },
    ],
  },
  {
    // Gerçek ekran görüntüsü/GIF hazır olana kadar ürünü kodla çizeriz —
    // boş kutu yerine çalışan bir arayüz görünür.
    key: 'mockVariant', label: 'Ürün Mockup\'ı (medya yokken çizilir)', type: 'select' as const,
    options: [
      { value: '',          label: 'Çizme (yalnız metin)' },
      { value: 'dashboard', label: 'Pano — KPI + gelir grafiği' },
      { value: 'calendar',  label: 'Takvim — randevular' },
      { value: 'crm',       label: 'Müşteri kartı — geçmiş' },
      { value: 'invoice',   label: 'Fatura — ödendi' },
      { value: 'website',   label: 'Site kurucu — bölümler' },
    ],
  },
  { key: 'mediaUrl',     label: 'Medya', type: 'image' as const },
  { key: 'mediaPoster',  label: 'Video Kapak Görseli (ops.)', type: 'image' as const },
  { key: 'mediaAlt',     label: 'Alternatif Metin (erişilebilirlik)', type: 'text' as const, max: 140 },
  { key: 'mediaCaption', label: 'Medya Açıklaması (ops.)', type: 'text' as const, max: 120 },
];
